import { useState, useEffect } from 'react'
import { PlusIcon, PencilIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { customerApi } from '../../services/api'
import toast from 'react-hot-toast'
import FAQEntryModal from './FAQEntryModal'
import CSVImport from './CSVImport'
import ConfirmDialog from '../common/ConfirmDialog'

export default function ManualFAQList() {
  const [faqs, setFaqs] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editFaq, setEditFaq] = useState(null)
  const [showCSV, setShowCSV] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const fetchFaqs = async () => {
    try {
      const res = await customerApi.get('/customer/knowledge-base/manual-entries')
      setFaqs(res.data.entries || [])
    } catch (err) {
      toast.error('Failed to load FAQs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFaqs()
  }, [])

  const handleAdd = async (data) => {
    try {
      await customerApi.post('/customer/knowledge-base/manual-entry', {
        entries: [data],
      })
      toast.success('FAQ added')
      fetchFaqs()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add FAQ')
      throw err
    }
  }

  const handleEdit = async (data) => {
    try {
      await customerApi.put(`/customer/knowledge-base/manual-entry/${editFaq.id}`, data)
      toast.success('FAQ updated')
      setEditFaq(null)
      fetchFaqs()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update FAQ')
      throw err
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await customerApi.delete(`/customer/knowledge-base/manual-entry/${deleteTarget.id}`)
      toast.success('FAQ deleted')
      setDeleteTarget(null)
      fetchFaqs()
    } catch (err) {
      toast.error('Failed to delete FAQ')
    }
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
            <div className="h-4 w-3/4 bg-slate-100 rounded mb-2" />
            <div className="h-3 w-1/2 bg-slate-50 rounded" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">Manual FAQs</h4>
          <p className="text-xs text-slate-500">{faqs.length} entries</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowCSV(true)}
            className="flex items-center space-x-1.5 px-3 py-2 border border-slate-200 text-xs font-medium text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <ArrowUpTrayIcon className="h-3.5 w-3.5" />
            <span>Import CSV</span>
          </button>
          <button
            onClick={() => { setEditFaq(null); setShowModal(true) }}
            className="flex items-center space-x-1.5 px-3 py-2 bg-brand-600 text-white text-xs font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            <span>Add FAQ</span>
          </button>
        </div>
      </div>

      {/* FAQ List */}
      {faqs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <PlusIcon className="h-7 w-7 text-slate-300" />
          </div>
          <h4 className="text-sm font-medium text-slate-900 mb-1">No FAQs added yet</h4>
          <p className="text-xs text-slate-500 max-w-xs mx-auto">
            Add your first FAQ to help your assistant answer common questions.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <div key={faq.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow group">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 mr-4">
                  <p className="text-sm font-medium text-slate-900 mb-1">Q: {faq.question}</p>
                  <p className="text-xs text-slate-500 line-clamp-2">A: {faq.answer}</p>
                  {faq.category && (
                    <span className="inline-block mt-2 text-[10px] font-medium text-brand-600 bg-brand-50 px-2 py-0.5 rounded-full">
                      {faq.category}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => { setEditFaq(faq); setShowModal(true) }}
                    className="p-1.5 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(faq)}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <FAQEntryModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditFaq(null) }}
        onSubmit={editFaq ? handleEdit : handleAdd}
        editData={editFaq}
      />

      <CSVImport
        isOpen={showCSV}
        onClose={() => setShowCSV(false)}
        onImported={fetchFaqs}
      />

      {deleteTarget && (
        <ConfirmDialog
          title="Delete FAQ"
          message={`Are you sure you want to delete "${deleteTarget.question}"? This cannot be undone.`}
          confirmText="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          destructive
        />
      )}
    </div>
  )
}
